import { IconAlert } from '@belong/icons';
import { Button, Text } from '@belong/ui';
import type { ConsumerFailedPaymentModel } from './failed-payments.types';
import { FAILED_PAYMENTS_STRINGS } from './failed-payments-strings';
import {
  formatFailedPaymentsAmount,
  getFailedPaymentsCount,
  getFailedPaymentsTotal,
} from './failed-payments-utils';

type FailedPaymentsSectionProps = {
  failedPayments: ConsumerFailedPaymentModel[];
  onResolveClick: () => void;
  title?: string;
};

export function FailedPaymentsSection({
  failedPayments,
  onResolveClick,
  title = FAILED_PAYMENTS_STRINGS['section.title'],
}: FailedPaymentsSectionProps) {
  if (!failedPayments.length) return null;

  const total = getFailedPaymentsTotal(failedPayments);
  const count = getFailedPaymentsCount(failedPayments) || failedPayments.length;
  const canResolve = failedPayments.some((p) => p.canSelfResolve);
  const paymentLabel = count === 1 ? 'payment' : 'payments';

  return (
    <section
      className="flex flex-col items-end gap-sm rounded border border-solid border-red bg-[rgba(255,86,97,0.02)] p-sm"
      data-testid="failed-payments-section"
    >
      <div className="flex w-full flex-col gap-xs text-navy">
        <div className="flex w-full flex-col items-start gap-xs md:flex-row">
          <IconAlert
            className="shrink-0 text-red md:mt-2xs"
            height={24}
            width={26}
          />
          <Text
            variant="body"
            fontWeight="semibold"
            className="text-h3-sm leading-[normal] tracking-sm text-red md:text-h3-xl"
          >
            {title}
          </Text>
        </div>

        <Text
          variant="body"
          className="text-p1 leading-[normal] text-navy md:text-body"
        >
          <span className="font-semibold">{`${count} ${paymentLabel} totaling ${formatFailedPaymentsAmount(
            total,
          )} ${FAILED_PAYMENTS_STRINGS['section.summary.need_attention']} `}</span>
          {FAILED_PAYMENTS_STRINGS['section.summary.body']}{' '}
          {FAILED_PAYMENTS_STRINGS['section.summary.resolve']}
        </Text>
      </div>

      <div className="flex w-full justify-end">
        <Button
          size={{ _: 'fluid', sm: 'default' }}
          className="rounded"
          onClick={onResolveClick}
          disabled={!canResolve}
        >
          {FAILED_PAYMENTS_STRINGS['section.cta']}
        </Button>
      </div>
    </section>
  );
}
